import {
  AppleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RightOutlined,
  WindowsOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimAgent, deleteAgent, listAgents } from '../api';
import { useAuth } from '../auth/AuthContext';
import { resolveErrorMessage } from '../utils/errorMessage';

const isOnlineAgent = (agent) => agent?.is_online === true;

const FALLBACK_WINDOWS_ARTIFACT_DIRS = [
  '/artifacts/win',
  '/artifacts/agent/win',
  '/artifacts/agent/windows',
];
const FALLBACK_MAC_ARTIFACT_DIRS = [
  '/artifacts/mac',
  '/artifacts/agent/mac',
];

const parseFilenameFromSha256Text = (rawText) => {
  const text = String(rawText || '');
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return '';
  const first = lines[0];
  const parts = first.split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const rawName = String(parts[parts.length - 1] || '').replace(/^\*/, '').trim();
  const normalized = rawName.replace(/\\/g, '/');
  return normalized.split('/').pop() || '';
};

const deriveWindowsUrlFromMacUrl = (macUrl) => {
  const raw = String(macUrl || '').trim();
  if (!raw) return '';
  const match = raw.match(/AispireAgent-([0-9A-Za-z._-]+)\.dmg(?:\?.*)?$/i);
  if (!match) return '';
  const version = String(match[1] || '').trim();
  if (!version) return '';
  return raw
    .replace(/\/mac\//i, '/win/')
    .replace(/AispireAgent-[0-9A-Za-z._-]+\.dmg(?:\?.*)?$/i, `AispireAgent-Setup-${version}.exe`);
};

const extractFilenameFromUrl = (url, fallbackName) => {
  const raw = String(url || '').trim();
  if (!raw) return fallbackName;
  try {
    const parsed = new URL(raw, window.location.origin);
    const last = String(parsed.pathname || '').split('/').pop() || '';
    return decodeURIComponent(last || fallbackName);
  } catch {
    return fallbackName;
  }
};

const isCrossOriginUrl = (url) => {
  try {
    const parsed = new URL(String(url || '').trim(), window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const getExpectedInstallerExt = (filename = '') => {
  const lower = String(filename || '').toLowerCase();
  if (lower.endsWith('.dmg')) return '.dmg';
  if (lower.endsWith('.exe')) return '.exe';
  return '';
};

const isHtmlLikeResponse = (resp) => {
  const contentType = String(resp?.headers?.get('content-type') || '').toLowerCase();
  return contentType.includes('text/html') || contentType.includes('application/xhtml+xml');
};

const checkUrlExists = async (url, expectedExt = '') => {
  try {
    const headResp = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (headResp.ok) {
      if (expectedExt && isHtmlLikeResponse(headResp)) return false;
      return true;
    }
    if (headResp.status !== 405 && headResp.status !== 501) return false;
  } catch {
    // continue with range GET fallback
  }
  try {
    const rangeResp = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: { Range: 'bytes=0-0' },
    });
    const ok = rangeResp.ok || rangeResp.status === 206 || rangeResp.status === 416;
    if (!ok) return false;
    if (expectedExt && isHtmlLikeResponse(rangeResp)) return false;
    return true;
  } catch {
    return false;
  }
};

const resolveWindowsFallbackUrl = async (versionHint = '') => {
  for (const baseDir of FALLBACK_WINDOWS_ARTIFACT_DIRS) {
    try {
      const shaResp = await fetch(`${baseDir}/sha256.txt`, { cache: 'no-store' });
      if (!shaResp.ok) continue;
      const filename = parseFilenameFromSha256Text(await shaResp.text());
      if (!filename || !/\.exe$/i.test(filename)) continue;
      const candidateUrl = `${baseDir}/${filename}`;
      // eslint-disable-next-line no-await-in-loop
      if (await checkUrlExists(candidateUrl, '.exe')) return candidateUrl;
    } catch {
      // try next directory
    }
  }

  for (const baseDir of FALLBACK_WINDOWS_ARTIFACT_DIRS) {
    try {
      const dirResp = await fetch(`${baseDir}/`, { cache: 'no-store' });
      if (!dirResp.ok) continue;
      const html = await dirResp.text();
      const matches = Array.from(html.matchAll(/href=["']([^"']+\.exe)["']/gi));
      const filenames = matches
        .map((match) => String(match?.[1] || '').split('/').pop() || '')
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));
      if (filenames.length) {
        const candidateUrl = `${baseDir}/${filenames[0]}`;
        // eslint-disable-next-line no-await-in-loop
        if (await checkUrlExists(candidateUrl, '.exe')) return candidateUrl;
      }
    } catch {
      // try next directory
    }
  }

  const trimmedVersion = String(versionHint || '').trim();
  const candidateNames = [
    trimmedVersion ? `AispireAgent-Setup-${trimmedVersion}.exe` : '',
    'AispireAgent-Setup-latest.exe',
    'AispireAgent-Setup.exe',
  ].filter(Boolean);

  for (const baseDir of FALLBACK_WINDOWS_ARTIFACT_DIRS) {
    for (const filename of candidateNames) {
      const candidateUrl = `${baseDir}/${filename}`;
      // eslint-disable-next-line no-await-in-loop
      const exists = await checkUrlExists(candidateUrl, '.exe');
      if (exists) return candidateUrl;
    }
  }
  return '';
};

const resolveMacFallbackUrl = async (versionHint = '') => {
  for (const baseDir of FALLBACK_MAC_ARTIFACT_DIRS) {
    try {
      const shaResp = await fetch(`${baseDir}/sha256.txt`, { cache: 'no-store' });
      if (!shaResp.ok) continue;
      const filename = parseFilenameFromSha256Text(await shaResp.text());
      if (!filename || !/\.dmg$/i.test(filename)) continue;
      const candidateUrl = `${baseDir}/${filename}`;
      // eslint-disable-next-line no-await-in-loop
      if (await checkUrlExists(candidateUrl, '.dmg')) return candidateUrl;
    } catch {
      // try next directory
    }
  }

  for (const baseDir of FALLBACK_MAC_ARTIFACT_DIRS) {
    try {
      const dirResp = await fetch(`${baseDir}/`, { cache: 'no-store' });
      if (!dirResp.ok) continue;
      const html = await dirResp.text();
      const matches = Array.from(html.matchAll(/href=["']([^"']+\.dmg)["']/gi));
      const filenames = matches
        .map((match) => String(match?.[1] || '').split('/').pop() || '')
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));
      if (filenames.length) {
        const candidateUrl = `${baseDir}/${filenames[0]}`;
        // eslint-disable-next-line no-await-in-loop
        if (await checkUrlExists(candidateUrl, '.dmg')) return candidateUrl;
      }
    } catch {
      // try next directory
    }
  }

  const trimmedVersion = String(versionHint || '').trim();
  const candidateNames = [
    trimmedVersion ? `AispireAgent-${trimmedVersion}.dmg` : '',
    'AispireAgent-latest.dmg',
    'AispireAgent.dmg',
  ].filter(Boolean);

  for (const baseDir of FALLBACK_MAC_ARTIFACT_DIRS) {
    for (const filename of candidateNames) {
      const candidateUrl = `${baseDir}/${filename}`;
      // eslint-disable-next-line no-await-in-loop
      const exists = await checkUrlExists(candidateUrl, '.dmg');
      if (exists) return candidateUrl;
    }
  }
  return '';
};

const AgentManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentKeyword, setAgentKeyword] = useState('');
  const [agents, setAgents] = useState([]);
  const [onlineOnlyAgents, setOnlineOnlyAgents] = useState(false);
  const [activeTab, setActiveTab] = useState('install');
  const [claimingAgent, setClaimingAgent] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState('');
  const [claimAgentId, setClaimAgentId] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [manifestDownloads, setManifestDownloads] = useState({
    windows: '',
    mac: '',
    version: '',
  });
  const [fallbackDownloads, setFallbackDownloads] = useState({
    windows: '',
    mac: '',
  });

  const envWindowsDownloadUrl = String(process.env.REACT_APP_AGENT_WINDOWS_DOWNLOAD_URL || '').trim();
  const envMacDownloadUrl = String(process.env.REACT_APP_AGENT_MAC_DOWNLOAD_URL || '').trim();
  const manifestUrl = String(process.env.REACT_APP_AGENT_RELEASE_MANIFEST_URL || '/artifacts/agent/release-manifest.json').trim();

  const derivedWindowsUrl = useMemo(() => {
    const macSourceUrl = envMacDownloadUrl || String(manifestDownloads.mac || '').trim();
    return deriveWindowsUrlFromMacUrl(macSourceUrl);
  }, [envMacDownloadUrl, manifestDownloads.mac]);

  const downloadItems = useMemo(() => ([
    {
      key: 'windows',
      title: 'Windows Agent',
      icon: <WindowsOutlined />,
      desc: '用于运营同学在 Windows 本机执行立即发布和失败接管重跑。',
      url: envWindowsDownloadUrl || String(manifestDownloads.windows || '').trim() || String(derivedWindowsUrl || '').trim() || String(fallbackDownloads.windows || '').trim(),
      source: envWindowsDownloadUrl
        ? 'env'
        : (manifestDownloads.windows ? 'manifest' : (derivedWindowsUrl ? 'derived' : (fallbackDownloads.windows ? 'fallback' : ''))),
    },
    {
      key: 'mac',
      title: 'Mac Agent',
      icon: <AppleOutlined />,
      desc: '用于运营同学在 macOS 本机执行立即发布和失败接管重跑。',
      url: envMacDownloadUrl || String(manifestDownloads.mac || '').trim() || String(fallbackDownloads.mac || '').trim(),
      source: envMacDownloadUrl ? 'env' : (manifestDownloads.mac ? 'manifest' : (fallbackDownloads.mac ? 'fallback' : '')),
    },
  ]), [derivedWindowsUrl, envMacDownloadUrl, envWindowsDownloadUrl, fallbackDownloads.mac, fallbackDownloads.windows, manifestDownloads.mac, manifestDownloads.windows]);

  const filteredAgents = useMemo(() => (
    onlineOnlyAgents ? (agents || []).filter((item) => isOnlineAgent(item)) : (agents || [])
  ), [agents, onlineOnlyAgents]);

  const agentStats = useMemo(() => {
    const list = Array.isArray(agents) ? agents : [];
    const total = list.length;
    const online = list.filter((item) => isOnlineAgent(item)).length;
    const offline = Math.max(0, total - online);
    return {
      total,
      online,
      offline,
      onlineRate: total > 0 ? Math.round((online / total) * 100) : 0,
    };
  }, [agents]);

  const loadAgents = useCallback(async ({ silent = false } = {}) => {
    try {
      setAgentsLoading(true);
      const resp = await listAgents({
        page: 1,
        page_size: 100,
        keyword: String(agentKeyword || '').trim(),
        online_window_sec: 60,
        user_id: String(user?.user_id || '').trim(),
      });
      if (resp?.status === 'success') {
        setAgents(Array.isArray(resp.items) ? resp.items : []);
      } else if (!silent) {
        message.error(resolveErrorMessage(resp?.error_message, '加载 Agent 列表失败'));
      }
    } catch (error) {
      if (!silent) message.error(resolveErrorMessage(error?.message || String(error), '加载 Agent 列表失败'));
    } finally {
      setAgentsLoading(false);
    }
  }, [agentKeyword, user?.user_id]);

  useEffect(() => {
    loadAgents({ silent: true });
  }, [loadAgents]);

  useEffect(() => {
    let cancelled = false;
    const shouldLoadManifest = Boolean(manifestUrl) && (!envWindowsDownloadUrl || !envMacDownloadUrl);
    if (!shouldLoadManifest) return undefined;

    const loadManifest = async () => {
      try {
        const response = await fetch(manifestUrl, { cache: 'no-store' });
        if (!response.ok) return;
        const payload = await response.json();
        if (cancelled || !payload || typeof payload !== 'object') return;
        let windowsUrl = String(payload?.windows?.url || '').trim();
        let macUrl = String(payload?.mac?.url || '').trim();
        if (windowsUrl.startsWith('/')) {
          const exists = await checkUrlExists(windowsUrl, '.exe');
          if (!exists) windowsUrl = '';
        }
        if (macUrl.startsWith('/')) {
          const exists = await checkUrlExists(macUrl, '.dmg');
          if (!exists) macUrl = '';
        }
        if (cancelled) return;
        setManifestDownloads({
          windows: windowsUrl,
          mac: macUrl,
          version: String(payload?.version || '').trim(),
        });
      } catch {
        // manifest is optional, ignore fetch failure
      }
    };

    loadManifest();
    return () => {
      cancelled = true;
    };
  }, [envMacDownloadUrl, envWindowsDownloadUrl, manifestUrl]);

  useEffect(() => {
    let cancelled = false;
    const shouldResolveWindowsFallback = !envWindowsDownloadUrl && !String(manifestDownloads.windows || '').trim();
    if (!shouldResolveWindowsFallback) return undefined;

    const resolveFallback = async () => {
      const url = await resolveWindowsFallbackUrl(manifestDownloads.version);
      if (cancelled) return;
      setFallbackDownloads((prev) => ({ ...prev, windows: String(url || '').trim() }));
    };

    resolveFallback();
    return () => {
      cancelled = true;
    };
  }, [envWindowsDownloadUrl, manifestDownloads.version, manifestDownloads.windows]);

  useEffect(() => {
    let cancelled = false;
    const shouldResolveMacFallback = !envMacDownloadUrl && !String(manifestDownloads.mac || '').trim();
    if (!shouldResolveMacFallback) return undefined;

    const resolveFallback = async () => {
      const url = await resolveMacFallbackUrl(manifestDownloads.version);
      if (cancelled) return;
      setFallbackDownloads((prev) => ({ ...prev, mac: String(url || '').trim() }));
    };

    resolveFallback();
    return () => {
      cancelled = true;
    };
  }, [envMacDownloadUrl, manifestDownloads.mac, manifestDownloads.version]);

  const downloadInstaller = useCallback(async (url, fallbackFilename) => {
    const targetUrl = String(url || '').trim();
    if (!targetUrl) {
      message.warning('下载地址不可用');
      return;
    }
    const filename = extractFilenameFromUrl(targetUrl, fallbackFilename);
    const expectedExt = getExpectedInstallerExt(filename);
    const isRelativeUrl = targetUrl.startsWith('/');
    const isCrossOrigin = isCrossOriginUrl(targetUrl);
    if (isRelativeUrl && expectedExt) {
      const exists = await checkUrlExists(targetUrl, expectedExt);
      if (!exists) {
        message.error('安装包链接失效，请刷新页面后重试');
        return;
      }
    }
    // For same-origin URLs, prefer native anchor download.
    // It is more reliable on macOS browsers than fetch->blob for large installers.
    if (!isCrossOrigin) {
      const link = document.createElement('a');
      link.href = targetUrl;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    try {
      const resp = await fetch(targetUrl, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`http ${resp.status}`);
      if (expectedExt && isHtmlLikeResponse(resp)) throw new Error('unexpected html');
      const blob = await resp.blob();
      if (expectedExt && String(blob.type || '').toLowerCase().includes('text/html')) throw new Error('unexpected html blob');
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    } catch {
      if (isRelativeUrl) {
        message.error('安装包链接失效，请联系管理员更新发布清单');
        return;
      }
      // Fallback for cross-origin urls that block fetch without CORS.
      const link = document.createElement('a');
      link.href = targetUrl;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  const claimCurrentAgent = useCallback(async () => {
    const agentId = String(claimAgentId || '').trim();
    const code = String(claimCode || '').trim();
    if (!agentId) {
      message.warning('请先输入认领ID');
      return;
    }
    if (!code) {
      message.warning('请先输入认领码');
      return;
    }
    try {
      setClaimingAgent(true);
      const resp = await claimAgent({
        agent_id: agentId,
        claim_code: code,
      });
      if (resp?.status === 'success') {
        message.success('认领成功');
        setActiveTab('agents');
        setClaimCode('');
        await loadAgents({ silent: false });
      } else if (resp?.status === 'not_found') {
        message.error('认领失败：服务端未找到该 Agent，请确认托盘中的认领ID并等待心跳上报后重试');
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '认领失败'));
      }
    } catch (error) {
      if (Number(error?.status || 0) === 404) {
        message.error('认领失败：后端缺少 /api/agents/claim 接口，请先部署后端新版本');
        return;
      }
      message.error(resolveErrorMessage(error?.message || String(error), '认领失败'));
    } finally {
      setClaimingAgent(false);
    }
  }, [claimAgentId, claimCode, loadAgents]);

  const handleDeleteAgent = useCallback(async (row) => {
    const agentId = String(row?.agent_id || '').trim();
    if (!agentId) return;
    if (isOnlineAgent(row)) {
      message.warning('在线 Agent 暂不允许删除，请先停止并等待离线');
      return;
    }
    try {
      setDeletingAgentId(agentId);
      const resp = await deleteAgent({ agent_id: agentId });
      if (resp?.status === 'success') {
        const cleared = Number(resp?.cleared_accounts || 0);
        message.success(cleared > 0 ? `已删除并解绑 ${cleared} 个关联账号` : '删除成功');
        await loadAgents({ silent: false });
      } else if (resp?.status === 'forbidden') {
        message.error(resolveErrorMessage(resp?.error_message, '无权限删除该 Agent'));
      } else if (resp?.status === 'not_found') {
        message.warning('该 Agent 已不存在，列表将刷新');
        await loadAgents({ silent: false });
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '删除 Agent 失败'));
      }
    } catch (error) {
      message.error(resolveErrorMessage(error?.message || String(error), '删除 Agent 失败'));
    } finally {
      setDeletingAgentId('');
    }
  }, [loadAgents]);

  const installTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="云端 + 本地双轨说明"
        description="定时任务默认由云端 Agent 执行；本地 Agent 用于立即发布和云端异常后的人工接管重跑。Agent 超过 60 秒无心跳会显示离线，离线后可手动删除清理。账号绑定和会话管理已收敛到“账号管理”。"
      />
      <Card size="small">
        <Space wrap>
          <Button type="primary" icon={<RightOutlined />} onClick={() => navigate('/users')}>
            去账号管理（绑定/会话）
          </Button>
        </Space>
      </Card>
      <Card title="认领本机 Agent" size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            在本机托盘菜单查看“认领ID/认领码”，填入后即可将该设备绑定到当前登录账号。
          </Typography.Text>
          <Space wrap>
            <Input
              allowClear
              placeholder="认领ID（agent_xxx）"
              value={claimAgentId}
              onChange={(e) => setClaimAgentId(e.target.value)}
              style={{ width: 320 }}
            />
            <Input
              allowClear
              placeholder="认领码（6位）"
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value)}
              onPressEnter={() => claimCurrentAgent()}
              style={{ width: 180 }}
            />
            <Button type="primary" loading={claimingAgent} onClick={() => claimCurrentAgent()}>
              认领
            </Button>
          </Space>
        </Space>
      </Card>
      <Row gutter={[12, 12]}>
        {downloadItems.map((item) => {
          const ready = Boolean(item.url);
          return (
            <Col key={item.key} xs={24} md={12}>
              <Card title={<Space>{item.icon}<span>{item.title}</span></Space>} size="small">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Typography.Text type="secondary">{item.desc}</Typography.Text>
                  {!ready ? <Tag color="orange">安装包暂未就绪</Tag> : null}
                  <Space>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      disabled={!ready}
                      onClick={() => downloadInstaller(item.url, `${item.key}-agent-installer`)}
                    >
                      下载
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Card title="安装指引" size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text>1. 下载并安装对应系统 Agent，安装后直接启动。</Typography.Text>
          <Typography.Text>2. 启动后打开本网站，“Agent列表”会自动识别并显示在线状态（通常 10-30 秒内）。</Typography.Text>
          <Typography.Text>3. 看到在线后，去“账号管理”完成账号绑定与会话检查。</Typography.Text>
        </Space>
      </Card>
    </Space>
  );

  const agentTableTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Agent总数" value={agentStats.total} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="在线" value={agentStats.online} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="离线" value={agentStats.offline} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="在线率" value={agentStats.onlineRate} suffix="%" />
          </Card>
        </Col>
      </Row>
      <Space>
        <Input
          allowClear
          placeholder="按 Agent ID / 主机名搜索"
          value={agentKeyword}
          onChange={(e) => setAgentKeyword(e.target.value)}
          onPressEnter={() => loadAgents({ silent: false })}
          style={{ width: 260 }}
        />
        <Button type="primary" onClick={() => loadAgents({ silent: false })}>查询</Button>
        <Button icon={<ReloadOutlined />} onClick={() => loadAgents({ silent: false })}>刷新</Button>
        <Space size={4}>
          <Switch checked={onlineOnlyAgents} onChange={setOnlineOnlyAgents} />
          <Typography.Text>仅看在线 Agent</Typography.Text>
        </Space>
      </Space>
      <Table
        size="small"
        rowKey={(row) => String(row?.agent_id || '')}
        loading={agentsLoading}
        dataSource={filteredAgents}
        pagination={false}
        columns={[
          {
            title: 'Agent',
            dataIndex: 'agent_id',
            key: 'agent_id',
            width: 280,
            render: (value, row) => {
              const host = String(row?.hostname || '').trim();
              return (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{host || value || '-'}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{value || '-'}</Typography.Text>
                </Space>
              );
            },
          },
          {
            title: '状态',
            key: 'status',
            width: 110,
            render: (_, row) => (
              <Tag color={isOnlineAgent(row) ? 'green' : 'default'}>
                {isOnlineAgent(row) ? '在线' : '离线'}
              </Tag>
            ),
          },
          {
            title: '系统',
            key: 'os',
            width: 140,
            render: (_, row) => {
              const os = String(row?.os || '-').trim();
              const arch = String(row?.arch || '-').trim();
              return `${os}/${arch}`;
            },
          },
          {
            title: '版本',
            dataIndex: 'agent_version',
            key: 'agent_version',
            width: 100,
            render: (value) => value || '-',
          },
          {
            title: '用户',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 120,
            render: (value) => value || '-',
          },
          {
            title: '最近心跳',
            dataIndex: 'last_seen_at',
            key: 'last_seen_at',
            width: 210,
            render: (value, row) => value || row?.last_heartbeat_at || '-',
          },
          {
            title: '操作',
            key: 'actions',
            width: 120,
            render: (_, row) => {
              const agentId = String(row?.agent_id || '');
              const deleting = deletingAgentId && deletingAgentId === agentId;
              const online = isOnlineAgent(row);
              return (
                <Popconfirm
                  title="删除 Agent"
                  description="仅建议删除长期离线设备。删除后会自动解绑该设备关联账号。"
                  okText="删除"
                  cancelText="取消"
                  onConfirm={() => handleDeleteAgent(row)}
                  disabled={online}
                >
                  <Button danger size="small" loading={deleting} disabled={online || deleting}>
                    删除
                  </Button>
                </Popconfirm>
              );
            },
          },
        ]}
      />
    </Space>
  );

  return (
    <div className="page-shell">
      <Card className="page-card" bodyStyle={{ padding: 12, overflowY: 'auto', overflowX: 'hidden' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'install', label: '下载安装', children: installTab },
            { key: 'agents', label: 'Agent列表', children: agentTableTab },
          ]}
        />
      </Card>
    </div>
  );
};

export default AgentManagement;
